import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { userRoles } from 'util/appConstants';
import { useActions } from 'hooks/useActions';
import { Images } from 'util/images';
import * as actions from './actions';
import CommentEditor from './commentEditor';
import FooterButton from './footerButton';
import CommentList from './commentList';
import CommentArchiveModal from './commentArchiveModal';
import CommentReportModal from './commentReportModal';
import {
  CommentContainer,
  CommentThumbnailColumn,
  CommentThumbnail,
  ThumbnailLineBox,
  ThumbnailLine,
  ThumbnailLineSpacer,
  CommentColumn,
  CommentPoster,
  DateText,
  AlignBox,
  AlignBoxLeft,
  AlignBoxRight,
  FooterAlignBox,
} from './styles';

const getReplyButtonText = (comment, viewReplies) => {
  let text = viewReplies ? 'Hide ' : 'View ';
  if (comment?.responseCount > 1) {
    return text + comment.responseCount + ' Replies';
  }
  return text + 'Reply';
};

export default function CommentDisplay({ commentId, commentLevel = 0 }) {
  const { userId, isCommunityUser, userRole } = useSelector((state) => state.auth);
  const {
    // Destructuring to get only nested values
    allComments: { [commentId]: comment },
    responseCommentsLoading: { [commentId]: childrenLoading },
    responseCommentIds: { [commentId]: childCommentIds },
  } = useSelector((state) => state.comments);
  const { getResponseComments, initPostComment, initReportComment, initArchiveComment } = useActions(actions);

  const niceDate = moment(comment?.createdAt).format('MMM Do YY');
  const profileLink = '/profile?id=' + comment.account;
  const avatar =
    comment?.accountThumbnail && comment.accountThumbnail !== '/images/placeholder-avatar-thumbnail.png'
      ? comment.accountThumbnail
      : Images.defaultAvatar;

  const [viewReplies, setViewReplies] = useState(true);
  const [viewRepliesButton, setViewRepliesButton] = useState(!!comment?.responseCount && commentLevel < 3);
  const [viewRepliesButtonText, setViewRepliesButtonText] = useState(getReplyButtonText(comment, viewReplies));

  const [viewPostReply, setViewPostReply] = useState(false);
  const [viewPostReplyButton, setViewPostReplyButton] = useState(isCommunityUser && commentLevel < 2);

  const [viewReportModal, setViewReportModal] = useState(false);
  const [viewArchiveModal, setViewArchiveModal] = useState(false);

  const [replyCommentLevel, setReplyCommentLevel] = useState(commentLevel + 1);

  useEffect(() => {
    if (commentLevel + 1 !== replyCommentLevel) setReplyCommentLevel(commentLevel + 1);
  }, [commentLevel, replyCommentLevel]);

  useEffect(() => {
    setViewPostReplyButton(isCommunityUser && replyCommentLevel < 3);
    if (!isCommunityUser) {
      setViewPostReply(false);
    }
  }, [isCommunityUser, replyCommentLevel]);

  useEffect(() => {
    if (viewReplies && comment.responseCount <= 0) {
      setViewReplies(false);
      setViewRepliesButton(false);
    } else if (comment.responseCount > 0) {
      setViewRepliesButtonText(getReplyButtonText(comment, viewReplies));
      setViewRepliesButton(true);
    }
  }, [viewReplies, comment]);

  useEffect(() => {
    if (viewReplies && comment?.responseCount) {
      getResponseComments(comment._id);
    }
  }, [viewReplies, comment, getResponseComments]);

  const onPostSuccess = () => {
    setViewPostReply(false);
    if (!viewReplies) {
      setViewReplies(true);
    }
  };

  return (
    comment && (
      <CommentContainer isRootComment={commentLevel === 0}>
        <CommentThumbnailColumn>
          <CommentThumbnail accountThumbnail={avatar} />
          {viewReplies && (
            <ThumbnailLineBox>
              <ThumbnailLine />
            </ThumbnailLineBox>
          )}
          <ThumbnailLineSpacer />
        </CommentThumbnailColumn>
        <CommentColumn>
          <AlignBox>
            <AlignBoxLeft>
              <CommentPoster href={profileLink}>{comment.accountName}</CommentPoster>
            </AlignBoxLeft>
            <AlignBoxRight>
              <DateText>{niceDate}</DateText>
            </AlignBoxRight>
          </AlignBox>
          <div dangerouslySetInnerHTML={{ __html: comment.text }} />
          <FooterAlignBox>
            <AlignBoxLeft>
              {viewPostReplyButton && (
                <FooterButton
                  text="Reply"
                  icon="fa fa-comment-o"
                  onClick={() => {
                    initPostComment(commentId);
                    setViewPostReply(!viewPostReply);
                  }}
                />
              )}
              {viewRepliesButton && (
                <FooterButton
                  text={viewRepliesButtonText}
                  icon="fa fa-comments-o"
                  onClick={() => {
                    setViewReplies(!viewReplies);
                  }}
                />
              )}
            </AlignBoxLeft>
            <AlignBoxRight>
              {(userId === comment.account || userRole === userRoles.SUPER_ADMIN) && (
                <FooterButton
                  text="Archive"
                  icon="fa fa-trash-o"
                  hoverColor="red"
                  onClick={() => {
                    initArchiveComment();
                    setViewArchiveModal(!viewArchiveModal);
                  }}
                />
              )}
              {userId && userId !== comment.account && (
                <FooterButton
                  text="Report"
                  icon="fa fa-fire-extinguisher"
                  hoverColor="red"
                  onClick={() => {
                    initReportComment();
                    setViewReportModal(!viewReportModal);
                  }}
                />
              )}
            </AlignBoxRight>
          </FooterAlignBox>
          {viewPostReply && (
            <CommentEditor
              onCancel={() => setViewPostReply(false)}
              onPostSuccess={onPostSuccess}
              testId={comment.test}
              contestId={comment.contest}
              respondsTo={comment._id}
            />
          )}
          {viewReplies && (
            <CommentList commentIds={childCommentIds} loading={childrenLoading} commentLevel={replyCommentLevel} />
          )}
          {viewArchiveModal && <CommentArchiveModal comment={comment} closeModal={() => setViewArchiveModal(false)} />}
          {viewReportModal && <CommentReportModal comment={comment} closeModal={() => setViewReportModal(false)} />}
        </CommentColumn>
      </CommentContainer>
    )
  );
}
